import React, { useState } from 'react';
import { StepHeader } from '../ui/StepHeader.jsx';
import { StepIntro } from '../ui/StepIntro.jsx';
import { Icon } from '../ui/Icon.jsx';
import { FormError } from '../ui/FormError.jsx';
import { IS_DEV } from '../utils/env.js';
import { maskZipCode, onlyDigits } from '../utils/masks.js';
import { getAddressByZipCode } from '../utils/acquisitionFormApi.js';

const BR_STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA',
  'MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN',
  'RS','RO','RR','SC','SP','SE','TO'
];

// Canonical Brazilian address fields, in display order (CEP first so ViaCEP can
// autofill the rest). Country is assumed BR and not collected.
const ADDRESS_FIELDS = ['ZipCode', 'Street', 'Number', 'Neighbour', 'Complement', 'City', 'State'];

const FIELD_CONFIG = {
  ZipCode:    { label: 'CEP',         type: 'text',   autoComplete: 'postal-code', placeholder: '00000-000', maxLength: 9 },
  Street:     { label: 'Logradouro',  type: 'text',   autoComplete: 'address-line1', maxLength: 150 },
  Number:     { label: 'Número',      type: 'text',   autoComplete: 'address-line2', optional: true, maxLength: 10 },
  Neighbour:  { label: 'Bairro',      type: 'text',   autoComplete: 'address-level3', maxLength: 80 },
  Complement: { label: 'Complemento', type: 'text',   autoComplete: 'address-line2', optional: true, maxLength: 100 },
  City:       { label: 'Cidade',      type: 'text',   autoComplete: 'address-level2', maxLength: 70 },
  State:      { label: 'UF',          type: 'select', autoComplete: 'address-level1' }
};

// Dev fill datasets (valid CEPs). A random group is picked per click.
const FAKE_ADDRESSES = [
  { ZipCode: '01001-000', Street: 'Praça da Sé',               Number: '100',  Neighbour: 'Sé',               Complement: 'Apto 1',  City: 'São Paulo',      State: 'SP' },
  { ZipCode: '20040-002', Street: 'Av. Rio Branco',            Number: '150',  Neighbour: 'Centro',           Complement: '',        City: 'Rio de Janeiro', State: 'RJ' },
  { ZipCode: '30130-010', Street: 'Av. Afonso Pena',           Number: '1000', Neighbour: 'Centro',           Complement: 'Sala 5',  City: 'Belo Horizonte', State: 'MG' },
  { ZipCode: '70040-010', Street: 'Esplanada dos Ministérios', Number: '1',    Neighbour: 'Zona Cívica',      Complement: '',        City: 'Brasília',       State: 'DF' },
  { ZipCode: '40020-000', Street: 'Av. Sete de Setembro',      Number: '200',  Neighbour: 'Comércio',         Complement: '',        City: 'Salvador',       State: 'BA' },
  { ZipCode: '80010-000', Street: 'Rua XV de Novembro',        Number: '300',  Neighbour: 'Centro',           Complement: 'Apto 12', City: 'Curitiba',       State: 'PR' },
  { ZipCode: '90010-150', Street: 'Rua dos Andradas',          Number: '400',  Neighbour: 'Centro Histórico', Complement: '',        City: 'Porto Alegre',   State: 'RS' },
  { ZipCode: '50010-000', Street: 'Av. Guararapes',            Number: '50',   Neighbour: 'Santo Antônio',    Complement: '',        City: 'Recife',         State: 'PE' },
  { ZipCode: '60060-000', Street: 'Rua Major Facundo',         Number: '500',  Neighbour: 'Centro',           Complement: 'Sala 2',  City: 'Fortaleza',      State: 'CE' },
  { ZipCode: '69005-070', Street: 'Av. Eduardo Ribeiro',       Number: '600',  Neighbour: 'Centro',           Complement: '',        City: 'Manaus',         State: 'AM' }
];

/**
 * Address step — Brazilian address fields, standard labelled inputs. Whether the
 * address is mandatory depends on which step is being rendered, so the caller
 * decides it through isRequired; an optional pass shows the skip button.
 */
export function AddressStep({ formData, onChange, onNext, onSkip, intro, isRequired = false, onBack, onClose, isInline, isPreview, isSubmitting, canGoBack }) {
  const [errors, setErrors] = useState({});

  const fields = ADDRESS_FIELDS;
  const addr = formData.address || {};
  const [cepStatus, setCepStatus] = useState(null); // 'loading' | 'notfound' | null

  function handleChange(name, value) {
    const next = name === 'ZipCode' ? maskZipCode(value)
      : name === 'Number' ? onlyDigits(value).slice(0, 10)
      : value;
    onChange({ address: { ...addr, [name]: next } });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'ZipCode' && onlyDigits(next).length === 8) {
      lookupCep(next);
    }
  }

  // Autocomplete the address from the CEP through our own API. Fills Street/
  // Neighbour/City/State (and Complement when empty) — including fields not shown
  // in the form (e.g. Neighbour), which the backend still requires for a Boleto charge.
  async function lookupCep(rawCep) {
    const cep = String(rawCep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepStatus('loading');
    try {
      const data = await getAddressByZipCode(cep);
      setCepStatus(null);
      onChange({
        address: {
          ...addr,
          ZipCode: maskZipCode(cep),
          Street: data.street || addr.Street || '',
          Neighbour: data.neighbour || addr.Neighbour || '',
          City: data.city || addr.City || '',
          State: data.state || addr.State || '',
          Complement: addr.Complement || data.complement || ''
        }
      });
      setErrors(prev => ({ ...prev, Street: '', Neighbour: '', City: '', State: '' }));
    } catch {
      setCepStatus('notfound');
    }
  }

  function validate() {
    const errs = {};
    if (addr.Number && Number(addr.Number) <= 0) {
      errs.Number = 'Número inválido';
    }
    if (!isRequired) return errs;
    for (const key of fields) {
      if (FIELD_CONFIG[key].optional) continue;
      if (!String(addr[key] || '').trim()) {
        errs[key] = `${FIELD_CONFIG[key].label} é obrigatório`;
      }
    }
    return errs;
  }

  async function handleContinue() {
    if (!isPreview) {
      const errs = validate();
      if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    }
    await onNext();
  }

  function fillFake() {
    const group = FAKE_ADDRESSES[Math.floor(Math.random() * FAKE_ADDRESSES.length)];
    onChange({ address: { ...addr, ...group } });
    setErrors({});
  }

  // Progressive disclosure: only the CEP shows until it's complete (or the
  // address already has data — e.g. returning to the step or a failed lookup
  // being filled by hand).
  const showFields = onlyDigits(addr.ZipCode || '').length === 8
    || fields.some(key => key !== 'ZipCode' && String(addr[key] || '').trim() !== '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepHeader
        onClose={onClose}
        showClose={!isInline}
        onDevFill={IS_DEV && !isPreview ? fillFake : undefined}
      />

      <div className="tdw-step-body">
        <StepIntro
          title="Seu endereço"
          subtitle={intro || 'Informe o CEP e completamos o resto para você.'}
        />

        <div className="tdw-field-row">
          {renderField('ZipCode', addr, errors, handleChange, cepStatus)}
          {cepStatus === 'loading' && (
            <span className="tdw-cep-status">Buscando endereço…</span>
          )}
        </div>

        {showFields && (
          <>
            {renderField('Street', addr, errors, handleChange, cepStatus)}
            <div className="tdw-field-row tdw-field-row-number">
              {renderField('Number', addr, errors, handleChange, cepStatus)}
              {renderField('Complement', addr, errors, handleChange, cepStatus)}
            </div>
            <div className="tdw-field-row tdw-field-row-city">
              {renderField('Neighbour', addr, errors, handleChange, cepStatus)}
              {renderField('City', addr, errors, handleChange, cepStatus)}
              {renderField('State', addr, errors, handleChange, cepStatus)}
            </div>
          </>
        )}

        <div style={{ flex: 1 }} />

        <FormError message={formData.apiError} />

        <button
          type="button"
          className="tdw-btn-cta"
          onClick={handleContinue}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Aguarde...' : 'Continuar'}
        </button>

        {!isRequired && (
          <button
            type="button"
            className="tdw-btn-link"
            onClick={onSkip || onNext}
            disabled={isSubmitting}
          >
            Pular esta etapa
          </button>
        )}

        {canGoBack && (
          <button
            type="button"
            className="tdw-btn-link"
            onClick={onBack}
            disabled={isSubmitting}
          >
            ← Voltar
          </button>
        )}
      </div>
    </div>
  );
}

function renderField(key, addr, errors, handleChange, cepStatus) {
  const cfg = FIELD_CONFIG[key];
  if (!cfg) return null;

  const value = addr[key] || '';

  if (cfg.type === 'select') {
    const options = BR_STATES.map(s => ({ value: s, label: s }));

    return (
      <div key={key} className="tdw-field">
        <label className="tdw-label">{cfg.label}</label>
        <div className="tdw-select-wrap">
          <select
            className={`tdw-input${errors[key] ? ' error' : ''}`}
            value={value}
            onChange={e => handleChange(key, e.target.value)}
            autoComplete={cfg.autoComplete}
          >
            <option value="">Selecionar</option>
            {options.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="tdw-select-arrow"><Icon name="chevronDown" size={16} /></span>
        </div>
        {errors[key] && <p className="tdw-field-error">{errors[key]}</p>}
      </div>
    );
  }

  return (
    <div key={key} className="tdw-field">
      <label className="tdw-label">{cfg.label}</label>
      <input
        type="text"
        className={`tdw-input${errors[key] ? ' error' : ''}`}
        placeholder={cfg.placeholder || cfg.label}
        value={value}
        onChange={e => handleChange(key, e.target.value)}
        autoComplete={cfg.autoComplete}
        inputMode={key === 'ZipCode' || key === 'Number' ? 'numeric' : undefined}
        maxLength={cfg.maxLength}
      />
      {errors[key] && <p className="tdw-field-error">{errors[key]}</p>}
      {key === 'ZipCode' && cepStatus === 'notfound' && (
        <p className="tdw-field-error">CEP não encontrado. Preencha o endereço manualmente.</p>
      )}
    </div>
  );
}
